import {
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    inject,
    input,
    signal
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
    Options,
    SelectSize,
    SpinnerSize,
    TapestrySelectModule,
    TapestrySpinnerModule
} from '@predictiveindex/tapestry';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { LanguageContextType } from '../../../constants-types';
import { InitDataService } from '../../../system-services';
import { TranslationService } from '../../../translations';

@Component({
    selector: 'language-selector',
    standalone: true,
    imports: [TapestrySelectModule, TapestrySpinnerModule, FormsModule],
    templateUrl: './language-selector.component.html',
    styleUrl: './language-selector.component.less',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LanguageSelectorComponent),
            multi: true
        }
    ]
})
export class LanguageSelectorComponent implements ControlValueAccessor {
    readonly #TR = inject(TranslationService);
    readonly #initData = inject(InitDataService);

    selectLabel = input<string>('');
    size = input<SelectSize>(SelectSize.Small);
    isRequired = input<boolean>(true);
    languageContext = input<LanguageContextType>(
        LanguageContextType.PI2SoftwareLanguages
    );

    loadingOptions = signal(true);

    spinnerSize: SpinnerSize = SpinnerSize.Small;

    private _value: string = '';
    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    languages = toSignal(
        toObservable(this.languageContext).pipe(
            switchMap((context) => this.fetchLanguagesByContext(context))
        )
    );

    private fetchLanguagesByContext(
        context: LanguageContextType
    ): Observable<Options[]> {
        let languageObservable: Observable<any[]>;

        switch (context) {
            case LanguageContextType.BehavioralAssessment:
            case LanguageContextType.CognitiveAssessment:
            case LanguageContextType.PI2SoftwareLanguages:
            default:
                languageObservable = this.#TR.fetchSupportedSiteLanguages();
                break;
        }

        return languageObservable.pipe(
            map((supportedLanguages) =>
                supportedLanguages.map<Options>((l) => ({
                    value: l.localeCode,
                    displayValue: l.displayName,
                    isDisabled: false
                }))
            ),
            tap(() => {
                if (!this._value) {
                    this._value = this.#initData.profile.defaultLanguageCode;
                    this.onChange(this._value);
                }
                this.loadingOptions.set(false);
            })
        );
    }

    get value(): string {
        return this._value;
    }

    set value(val: string) {
        this._value = val;
        this.onChange(val);
        this.onTouched();
    }

    onValueChange(newValue: string): void {
        this.value = newValue;
    }

    writeValue(value: string): void {
        if (value !== undefined && value !== null) {
            this._value = value;
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
}
